import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-searchable-checkbox-list',
  templateUrl: './searchable-checkbox-list.component.html',
  styles: ``,
  imports: [
    NgIf,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatListModule,
    TranslateModule,
    AsyncPipe,
    MatProgressSpinner,
    ReactiveFormsModule,
  ],
})
export class SearchableCheckboxListComponent<T extends { disabled?: boolean; id: string }> implements OnInit {
  @Input() public items!: Observable<T[]>;
  @Input() public filterKey: keyof T | null = null;
  @Input() public label = '';
  @Input() public sublabelKey: keyof T | null = null;

  @Output() public selectionChange = new EventEmitter<string[]>();

  public form!: FormGroup;

  private searchTerm$ = new BehaviorSubject<string>('');
  public filteredList$!: Observable<T[]>;
  public selectedItems: Set<string> = new Set<string>();

  public ngOnInit(): void {
    this.form = new FormGroup({
      items: new FormControl([]),
    });

    this.filteredList$ = combineLatest([this.items, this.searchTerm$.pipe(startWith(''))]).pipe(
      map(([items, searchTerm]) => {
        const selectedItems = items
          .filter((item) => item.disabled || this.selectedItems.has(item.id))
          .map((item) => item.id);
        this.form.get('items')?.setValue(selectedItems);

        return items
          .map((item) => ({
            ...item,
            disabled: item.disabled ?? false,
          }))
          .filter((item) => {
            const mainLabel =
              this.filterKey && typeof item[this.filterKey] === 'string'
                ? (item[this.filterKey] as string).toLowerCase()
                : '';
            const subLabel =
              this.sublabelKey && typeof item[this.sublabelKey] === 'string'
                ? (item[this.sublabelKey] as string).toLowerCase()
                : '';

            const combinedLabel = `${mainLabel} (${subLabel})`;
            return combinedLabel.includes(searchTerm.toLowerCase());
          });
      })
    );
  }

  public updateSearchTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm$.next(input?.value || '');
  }

  public toggleSelection(item: T): void {
    if (this.selectedItems.has(item.id)) {
      this.selectedItems.delete(item.id);
    } else {
      this.selectedItems.add(item.id);
    }

    this.selectionChange.emit(Array.from(this.selectedItems));
  }
}
